import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { DateTime } from 'luxon';
import { Institution } from '../entities/institution.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import { User } from '../entities/user.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { getNotificationType } from './_helper.js';

defineMailTemplate(MailTemplateType.WARTELISTE_VORRUECK, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Wartelisten Update',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>F&uuml;r eine Ihrer Anmeldungen, bei denen Sie aktuell auf der Warteliste stehen, sind Sie einen Platz nach vorne gerutscht:</p>
<p>{{changed-registrations}}</p>
<p>Falls Sie den Termin nicht wahrnehmen k&ouml;nnen, bitten wir Sie, sich zeitnah in ihrer Anmeldeverwaltung von der Veranstaltung abzumelden. Andere Studierende auf der Warteliste profitieren dann von Ihrer Abmeldung.</p>
<p>Wir freuen uns, wenn wir Sie in Zukunft in einer unserer Veranstaltungen begr&uuml;&szlig;en d&uuml;rfen und empfehlen Ihnen hierf&uuml;r unser Vortrags- und Workshop-Programm auf <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de.</a></p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>there's an update for one of your registrations where you are currently on the waiting list:</p>
<p>{{changed-registrations}}</p>
<p>You are unable to attend? Please deregister and thereby enable other students to participate.</p>
<p>We look forward to welcoming you to one of our events in the future and recommand our lecture and workshop programme at <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de</a> for this purpose</p>
<p>Best regards</p>`,
    footer: '',
  },
  variables: InstitutionMailTemplate.getAvailableVariables(MailTemplateType.WARTELISTE_VORRUECK),
  attachments: [],
  displayName: 'Warteliste Vorrücken',
  editorHint: () => InstitutionMailTemplate.getMailHint(MailTemplateType.WARTELISTE_VORRUECK),
});

export async function notifyWartelisteVorrueck(participationID) {
  const participation = await getByID(
    Participation,
    participationID,
  )({
    _id: 1,
    _class: 1,
    userID: 1,
    productID: 1,
    state: 1,
    waitingListPlace: 1,
    product: {
      _id: 1,
      _class: 1,
      name: 1,
      description: 1,
      sortingWeight: 1,
      hasPrice: 1,
      categoryID: 1,
      institutionID: 1,
      timeframeID: 1,
      lastReminderMailSent: 1,
      publishDate: 1,
      topic: 1,
      language: 1,
      presenceType: 1,
      isCanceled: 1,
      appointments: {
        _class: 1,
        _id: 1,
        start: 1,
        end: 1,
        isNonBlocking: 1,
        location: 1,
        name: 1,
      },
      capacity: 1,
      hideCapacity: 1,
    },
    user: { _id: 1, title: 1, firstName: 1, lastName: 1, email: 1, whatsappNotification: 1, whatsappPhone: 1 },
  });
  if (!participation) throw new Error('Couldnt find participation with ID ' + participationID);

  const user = participation.user;
  if (!user) throw new Error('no user is set');

  const institutionID = participation.product?.institutionID;
  const mailTemplateType = MailTemplateType.WARTELISTE_VORRUECK;
  const mailTemplate = await InstitutionMailTemplate.getMailTemplateForInstitution(
    institutionID,
    mailTemplateType,
  )(InstitutionMailTemplate.allFields);

  const institution = await getByID(Institution, institutionID)({ _id: 1 });
  if (!institution) throw new Error('institution not found ' + institutionID);

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const start = participation.product?.appointments?.[0]?.start;
  const daysTillEvent = start?.diff(DateTime.now()).as('days').toFixed(0);
  let dates = participation.product?.appointments?.map(a => a.start.toFormat('dd.LL.yyyy')).join(', ');
  if (!dates) dates = start?.toFormat('dd.LL.yyyy');

  const notificationType = getNotificationType(user, mailTemplate);
  // TODO: add whatsapp
  if (notificationType === 'whatsapp' && false) {
  } else if (notificationType === 'email') {
    const mailVars = {
      [MailTemplateVars.TITLE]: participation.user?.title ?? '',
      [MailTemplateVars.FIRST_NAME]: participation.user?.firstName,
      [MailTemplateVars.LAST_NAME]: participation.user?.lastName,
      [MailTemplateVars.EVENT_TITLE]: participation.product?.name,
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),

      [MailTemplateVars.CHANGED_REGISTRATIONS]: [
        InstitutionMailTemplate.mapChangedRegistrationVar({
          productBefore: participation.product.toJSON(),
          newState: participation.state,
          newWaitingListPlace: participation instanceof ParticipationWorkshop ? participation.waitingListPlace : null,
          participation,
          institution,
          englishVersion: false,
        }),
      ],
      [MailTemplateVars.CHANGED_REGISTRATIONS_EN]: [
        InstitutionMailTemplate.mapChangedRegistrationVar({
          productBefore: participation.product.toJSON(),
          newState: participation.state,
          newWaitingListPlace: participation instanceof ParticipationWorkshop ? participation.waitingListPlace : null,
          participation,
          institution,
          englishVersion: true,
        }),
      ],
      // TODO: set this correctly
      [MailTemplateVars.OTHER_REGISTRATIONS]: InstitutionMailTemplate.mapOtherRegistrationsVar({
        participations: [],
        institution,
        products: [],
        englishVersion: false,
      }),
      [MailTemplateVars.EVENT_DATES]: dates,
      [MailTemplateVars.EVENT_LEAD_DAYS]: daysTillEvent,
    } as MailVarContents;

    const content = await mailTemplate.buildEmail(mailVars as any);
    const subject = await mailTemplate.buildSubject(mailVars as any);

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    const fullEmailRec = {
      email: user.email,
      institutionID,
      content,
      subject,
      attachmentPaths: [] as string[],
    };

    await sendInstitutionEmail(fullEmailRec);
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}
